// Ported from Angular app: /app/directives/auth/permissions.ts
export const PermissionLevel = {
  NoAccess: 0,
  Read: 15,
  CreateEdit: 30,
  Delete: 45,
  Admin: 50,
} as const

export type PermissionLevel = (typeof PermissionLevel)[keyof typeof PermissionLevel]

export interface PermissionCheck {
  name: string
  level: PermissionLevel
}

// Permission names matching the backend permission matrix
export const Permissions = {
  // Cash Management
  CashPosition: 'CashManagement.CashPosition',
  Transactions: 'CashManagement.Transactions',
  Payments: 'CashManagement.Payments',
  // Split of `Payments` (CU-91.1)
  PaymentsBlotter: 'CashManagement.Payments.Blotter',
  PaymentsApprovals: 'CashManagement.Payments.Approvals',
  PaymentsNetSettlements: 'CashManagement.Payments.NetSettlements',
  Invoices: 'CashManagement.Invoices',
  ExportData: 'CashManagement.CashPosition.ExportData',
  BankStatements: 'CashManagement.CashPosition.BankStatements',
  TransactionsPosting: 'CashManagement.TransactionsPosting',
  // Split of `CashPosition` (CU-91.1)
  CashPositionForecast: 'CashManagement.CashPosition.Forecast',
  // Core Data
  BankAccounts: 'CoreData.BankAccounts',
  ExchangeRates: 'CoreData.ExchangeRates',
  CompanyGroups: 'CoreData.CompanyGroups',
  Companies: 'CoreData.Companies',
  Counterparties: 'CoreData.Counterparties',
  CounterpartiesApprove: 'CoreData.Counterparties.Approve',
  Tags: 'CoreData.Tags',
  MatchingRules: 'CoreData.MatchingRules',
  Ssis: 'CoreData.SSIs',
  CustomEntities: 'CoreData.CustomEntities',
  StaticData: 'CoreData.StaticData',
  // Admin
  UserManagement: 'AdminAndSettings.UserManagement',
  UserGroups: 'AdminAndSettings.UserGroups',
  Connectivity: 'AdminAndSettings.Connectivity',
  // Split of `UserManagement` (CU-91.1)
  AccessTokens: 'AdminAndSettings.AccessTokens',
  // Reporting (CU-91.1)
  ReportingBlotters: 'Reporting.Blotters',
  ReportingRisk: 'Reporting.Risk',
  ReportingRegulatory: 'Reporting.Regulatory',
  ReportingCustom: 'Reporting.Custom',
  // Accounting (CU-91.1)
  AccountingJournal: 'Accounting.Journal',
  AccountingCoaRules: 'Accounting.CoARules',
  AccountingErp: 'Accounting.ERP',
  AccountingAudit: 'Accounting.Audit',
  // Data (CU-91.1)
  DataHub: 'Data.Hub',
  DataRepository: 'Data.Repository',
  DataExport: 'Data.Export',
  DataFinancialEvents: 'Data.FinancialEvents',
  // Integrations (CU-91.1)
  IntegrationsServices: 'Integrations.Services',
  IntegrationsIngestion: 'Integrations.Ingestion',
  IntegrationsActivity: 'Integrations.Activity',
  IntegrationsApi: 'Integrations.Api',
  // Workflows, alerts and agents (CU-91.1)
  WorkflowsView: 'Workflows.View',
  WorkflowsApprovals: 'Workflows.Approvals',
  WorkflowsBuilder: 'Workflows.Builder',
  AlertsManage: 'Alerts.Manage',
  AgentsView: 'Agents.View',
  AgentsBuilder: 'Agents.Builder',
  AgentsApprovals: 'Agents.Approvals',
  // Config and platform (CU-91.1)
  ConfigParsingTemplates: 'Config.ParsingTemplates',
  OnboardingRoadmapBoard: 'Platform.OnboardingRoadmapBoard',
  // Reconciliation (TH.Reconciliation module via API gateway — UC-033)
  ReconciliationConciliations: 'Reconciliation.Conciliations',
  ReconciliationMatchingRules: 'Reconciliation.MatchingRules',
  ReconciliationMovements: 'Reconciliation.Movements',
  ReconciliationAlerts: 'Reconciliation.Alerts',
} as const

/**
 * Split key -> legacy key, mirroring `UserPermissionKeyAliases.cs` in th_saas (CU-91.1).
 * `canAccess` falls back to the legacy key while the split key is absent from the
 * user's permissions (before the saas deploy). Remove when saas retires the legacy keys.
 */
export const LEGACY_PERMISSION_ALIASES: Readonly<Record<string, string>> = {
  [Permissions.PaymentsBlotter]: Permissions.Payments,
  [Permissions.PaymentsApprovals]: Permissions.Payments,
  [Permissions.PaymentsNetSettlements]: Permissions.Payments,
  [Permissions.CashPositionForecast]: Permissions.CashPosition,
  [Permissions.AccessTokens]: Permissions.UserManagement,
}
